/**
 * Admin · Future Considerations
 * ------------------------------
 * Route: /admin/future-considerations
 *
 * Curated bookmarks of competitor moves, market signals, product
 * ideas, and strategy notes. Admin-only. Backed by
 * /api/admin/future-considerations.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Loader2, ExternalLink, Trash2, Tag, Filter,
  FileText, Sparkles, Handshake, Landmark, Building2, MoreHorizontal,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import { devWarn } from "@/lib/devlog";

const CATEGORIES = [
  { id: "market_signal",    label: "Market signal",    icon: Sparkles, tone: "blue"    },
  { id: "competitor_move",  label: "Competitor move",  icon: Building2, tone: "amber"  },
  { id: "partnership_idea", label: "Partnership idea", icon: Handshake, tone: "violet" },
  { id: "product_idea",     label: "Product idea",     icon: FileText,  tone: "emerald" },
  { id: "regulatory",       label: "Regulatory",       icon: Landmark,  tone: "rose"   },
  { id: "other",            label: "Other",            icon: MoreHorizontal, tone: "slate" },
];

const STATUSES = ["new", "reviewed", "actioned", "archived"];

const CAT_STYLE = {
  blue:    { bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-800",    dot: "bg-blue-500" },
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800",   dot: "bg-amber-500" },
  violet:  { bg: "bg-violet-50",  border: "border-violet-200",  text: "text-violet-800",  dot: "bg-violet-500" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
  rose:    { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-800",    dot: "bg-rose-500" },
  slate:   { bg: "bg-slate-50",   border: "border-slate-200",   text: "text-slate-700",   dot: "bg-slate-500" },
};

const STATUS_STYLE = {
  new:       "bg-blue-100 text-blue-800",
  reviewed:  "bg-slate-100 text-slate-700",
  actioned:  "bg-emerald-100 text-emerald-800",
  archived:  "bg-slate-100 text-slate-500",
};

function catStyleFor(catId) {
  const cat = CATEGORIES.find((c) => c.id === catId);
  return CAT_STYLE[cat?.tone || "slate"];
}

function AddDialog({ onSaved, onClose }) {
  const [form, setForm] = useState({
    title: "", summary: "", why_it_matters: "", source_url: "",
    source_name: "", category: "market_signal", tags: "", status: "new",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || form.title.trim().length < 3) {
      toast.error("Title required (3+ characters)");
      return;
    }
    if (!form.summary.trim() || form.summary.trim().length < 3) {
      toast.error("Summary required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        source_url: form.source_url.trim() || undefined,
        source_name: form.source_name.trim() || undefined,
        why_it_matters: form.why_it_matters.trim() || undefined,
      };
      await api.post("/admin/future-considerations", payload);
      toast.success("Added to Future Considerations");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="future-considerations-add-dialog"
    >
      <div
        className="w-full max-w-2xl bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-100">
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-blue-700 mb-1">Add to library</div>
          <h3 className="font-display text-xl font-bold text-slate-900">New future consideration</h3>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="One line that captures the signal"
              data-testid="fc-add-title"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Summary *</label>
            <textarea
              value={form.summary}
              onChange={(e) => update("summary", e.target.value)}
              placeholder="What happened and where you saw it"
              rows={3}
              data-testid="fc-add-summary"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Why it matters</label>
            <textarea
              value={form.why_it_matters}
              onChange={(e) => update("why_it_matters", e.target.value)}
              placeholder="How this changes strategy for Nodera or our clinics"
              rows={2}
              data-testid="fc-add-why"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Source URL</label>
              <input
                type="url"
                value={form.source_url}
                onChange={(e) => update("source_url", e.target.value)}
                placeholder="https://…"
                data-testid="fc-add-source-url"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Source name</label>
              <input
                type="text"
                value={form.source_name}
                onChange={(e) => update("source_name", e.target.value)}
                placeholder="e.g. Reuters, CVS press release"
                data-testid="fc-add-source-name"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Category</label>
              <select
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                data-testid="fc-add-category"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Tags (comma-separated)</label>
              <input
                type="text"
                value={form.tags}
                onChange={(e) => update("tags", e.target.value)}
                placeholder="cvs, glp-1, metabolic"
                data-testid="fc-add-tags"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-xs font-semibold text-slate-700 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="fc-add-save"
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Add to library
          </button>
        </div>
      </div>
    </div>
  );
}

function ItemRow({ item, onStatusChange, onDelete }) {
  const cat = CATEGORIES.find((c) => c.id === item.category);
  const Icon = cat?.icon || Sparkles;
  const style = catStyleFor(item.category);
  return (
    <div
      data-testid={`fc-row-${item.id}`}
      className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-sm transition-shadow"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${style.bg} ${style.text} border ${style.border}`}>
              <Icon className="w-3 h-3" />
              {cat?.label || item.category}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${STATUS_STYLE[item.status] || STATUS_STYLE.new}`}>
              {item.status}
            </span>
            {item.source_name && (
              <span className="text-[11px] text-slate-500">· {item.source_name}</span>
            )}
          </div>
          <h3 className="font-display text-base font-bold text-slate-900 leading-snug">
            {item.title}
          </h3>
          <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{item.summary}</p>
          {item.why_it_matters && (
            <div className="mt-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-blue-800 mb-0.5">Why it matters</div>
              <p className="text-xs text-blue-900 leading-relaxed">{item.why_it_matters}</p>
            </div>
          )}
          {item.tags && item.tags.length > 0 && (
            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
              <Tag className="w-3 h-3 text-slate-400" />
              {item.tags.map((t) => (
                <span key={t} className="text-[10.5px] font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                  {t}
                </span>
              ))}
            </div>
          )}
          <div className="mt-3 flex items-center gap-3 text-[10.5px] text-slate-400">
            {item.added_by && <span>By {item.added_by}</span>}
            {item.created_at && <span>· {new Date(item.created_at).toLocaleDateString()}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {item.source_url && (
            <a
              href={item.source_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`fc-source-${item.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
            >
              Source <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <select
            value={item.status}
            onChange={(e) => onStatusChange(item, e.target.value)}
            data-testid={`fc-status-${item.id}`}
            className="text-[11px] rounded-full border border-slate-200 px-2 py-1 font-semibold hover:border-slate-400 cursor-pointer"
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            type="button"
            onClick={() => onDelete(item)}
            data-testid={`fc-delete-${item.id}`}
            className="text-[11px] text-slate-400 hover:text-rose-600 inline-flex items-center gap-1"
            title="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminFutureConsiderations() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const r = await api.get("/admin/future-considerations", { params });
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      devWarn("[FutureConsiderations] load failed:", e);
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const onStatusChange = async (item, next) => {
    try {
      await api.patch(`/admin/future-considerations/${item.id}`, { status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/admin/future-considerations/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const statusButtons = useMemo(() => [
    { id: "",        label: `All (${counts.total || 0})` },
    { id: "new",     label: `New (${counts.new || 0})` },
    { id: "reviewed",label: `Reviewed (${counts.reviewed || 0})` },
    { id: "actioned",label: `Actioned (${counts.actioned || 0})` },
    { id: "archived",label: `Archived (${counts.archived || 0})` },
  ], [counts]);

  return (
    <ToolPageShell
      eyebrow="Admin · Strategy"
      title="Future Considerations"
      intro="Bookmarks and strategy notes — competitor moves, market signals, partnership ideas, product ideas, and regulatory events worth revisiting."
      actions={(
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          data-testid="fc-add-btn"
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 py-2 text-xs font-semibold"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      )}
    >
      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {statusButtons.map((s) => (
            <button
              key={s.id || "all"}
              type="button"
              onClick={() => setStatusFilter(s.id)}
              data-testid={`fc-filter-status-${s.id || "all"}`}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                statusFilter === s.id
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-500">
          <Filter className="w-3 h-3" />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            data-testid="fc-filter-category"
            className="rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold cursor-pointer"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-2xl" data-testid="fc-empty">
          <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-semibold text-slate-700">No entries yet</div>
          <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
            Bookmark market signals, competitor moves, and strategy notes worth revisiting.
          </p>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="mt-3 inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" /> Add your first
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              onStatusChange={onStatusChange}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      {showAdd && <AddDialog onClose={() => setShowAdd(false)} onSaved={load} />}
    </ToolPageShell>
  );
}
